// Product analytics: OpenPanel, self-hosted at a.solungo.com (docs/project.md §12). Everything the
// user does is sent: every screen with its real URL, the product events below with the words,
// queries and e-mails they concern, and a recording of the session (rrweb, text and inputs as they
// are). Events belong to the account: `identifyUser` names the OpenPanel profile with the user's
// id, e-mail and name, and the API and the worker send their own events under the same profile id
// (apps/api/src/common/analytics.ts). The privacy policy describes all of it (apps/web/src/legal.ts).
import { OpenPanel } from '@openpanel/web';
import type { UserRow } from '@solungo/core/schema';
import { browserLocale, pickUiLocale } from '@/i18n';
import { ANALYTICS_API_URL, ANALYTICS_CLIENT_ID, APP_RELEASE } from './env';
import { storage } from './storage';

// Where a word was added from: the omnibox, a word of the shared base suggested under it, the
// preview of a related word, the retry of a failed add in the list, the "manually" form
export type AddSource = 'search' | 'suggestion' | 'preview' | 'retry' | 'manual';

// Every product event with its properties; an event missing here cannot be sent.
export interface AnalyticsEvents {
  sign_in_code_requested: { resend: boolean; email: string };
  sign_in_failed: { step: 'email' | 'code'; reason: string; email: string };
  signed_in: { new_user: boolean; via_invite: boolean; email: string };
  signed_out: void;
  onboarding_completed: { name: string; target_lang: string; native_lang: string };
  dictionary_created: { target_lang: string; base_lang: string };
  dictionary_switched: { role: 'owner' | 'teacher'; to_dictionary_id: string; target_lang: string };
  search_opened: { surface: 'phone' | 'desktop' };
  // What the user typed into the omnibox once they paused, how many words of the dictionary it
  // found and how many of the shared base it offered to add
  searched: { query: string; results: number; suggestions: number };
  // rank: the position of the opened result, from 1
  search_result_opened: { rank: number; query: string; headword: string };
  hard_letter_inserted: { letter: string };
  // typed: what the user entered; headword: the spelling the card got (the worker may still
  // canonicalize a word that is building)
  word_added: {
    source: AddSource;
    result: 'ready' | 'building' | 'existing';
    typed: string;
    headword: string;
    entry_id: string;
  };
  word_add_failed: { source: AddSource; reason: string; typed: string };
  // details: the translations are on the card, examples and related words are still coming
  card_viewed: {
    entry_id: string;
    headword: string;
    state: 'ready' | 'details' | 'building' | 'failed';
  };
  card_browsed: { direction: 'prev' | 'next'; from_headword: string };
  related_word_opened: {
    kind: 'synonym' | 'antonym' | 'collocation';
    in_dictionary: boolean;
    headword: string;
    from_headword: string;
  };
  audio_played: { kind: 'word' | 'example'; headword: string; text: string };
  card_fill_retried: { ok: boolean; entry_id: string };
  // overridden / restored: the card fields, comma-separated, whose override the save set or dropped
  entry_edited: {
    headword: string;
    headword_changed: boolean;
    new_headword: string;
    overridden: string;
    restored: string;
  };
  entry_deleted: { entry_id: string; headword: string };
  teacher_invited: { email: string };
  share_revoked: { accepted: boolean; email: string };
  share_accepted: void;
  dictionary_left: void;
  profile_updated: { field: 'name' | 'ui_locale' | 'native_lang'; value: string };
  session_ended: void;
  app_installed: void;
  app_updated: void;
}

// Sent with every event from the moment it is known
export interface AnalyticsContext {
  ui_locale?: string;
  dictionary_id?: string;
  target_lang?: string;
  base_lang?: string;
  role?: 'owner' | 'teacher';
}

type Args<K extends keyof AnalyticsEvents> = AnalyticsEvents[K] extends void ? [] : [AnalyticsEvents[K]];

const client = ANALYTICS_CLIENT_ID
  ? new OpenPanel({
      clientId: ANALYTICS_CLIENT_ID,
      apiUrl: ANALYTICS_API_URL,
      trackOutgoingLinks: true,
      trackAttributes: true,
      sessionReplay: { enabled: true, maskAllInputs: false, maskAllText: false },
      // Offline the events and the recording wait in memory and go out, stamped with their own
      // time, once the network is back (the app works offline; its use there counts too)
      disabled: !navigator.onLine,
    })
  : null;

let screenPathname = '';
let identified = '';

export function initAnalytics(): void {
  if (!client) return;
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  client.setGlobalProperties({
    release: APP_RELEASE,
    display_mode: standalone ? 'standalone' : 'browser',
    // IntlShell keeps it current; this is its first value, before the first screen renders
    ui_locale: pickUiLocale(storage.getUser()?.locale, storage.getPendingLocale(), browserLocale()),
  });
  // The remembered user: a cold start's first screen already belongs to the profile
  const stored = storage.getUser();
  if (stored) identifyUser(stored);
  window.addEventListener('offline', () => {
    client.options.disabled = true;
  });
  window.addEventListener('online', () => client.ready());
  window.addEventListener('appinstalled', () => track('app_installed'));
}

// The signed-in user names the profile every event of this device goes to from now on. Called with
// every /me the app learns; repeats are dropped.
export function identifyUser(user: UserRow): void {
  // The offline stand-in (a device with dictionaries but no stored user) is nobody
  if (!client || !user.email) return;
  const profile = {
    profileId: user.id,
    email: user.email,
    ...(user.name ? { firstName: user.name } : {}),
    properties: { ui_locale: user.locale, native_lang: user.nativeLang, registered_at: user.createdAt },
  };
  const key = JSON.stringify(profile);
  if (key === identified) return;
  identified = key;
  void client.identify(profile);
}

// Signing out: the next events are nobody's until the next sign-in
export function forgetUser(): void {
  identified = '';
  client?.clear();
}

export function setAnalyticsContext(context: AnalyticsContext): void {
  client?.setGlobalProperties({ ...context });
}

// A resolved navigation: one screen view per pathname (search params, like the omnibox query, do
// not make a new screen) with the full URL, and the matched route's shape to group screens by.
export function trackScreen(pathname: string, fullPath: string): void {
  if (pathname === screenPathname) return;
  screenPathname = pathname;
  if (!client) {
    if (import.meta.env.DEV) console.debug('[analytics] screen_view', location.href);
    return;
  }
  client.screenView(location.href, { route: routeShape(fullPath), online: navigator.onLine });
}

export function track<K extends keyof AnalyticsEvents>(name: K, ...[props]: Args<K>): void {
  if (!client) {
    if (import.meta.env.DEV) console.debug('[analytics]', name, props ?? {});
    return;
  }
  void client.track(name, { ...props, online: navigator.onLine });
}

// '/d/$dictionaryId/w/$entryId/' → '/d/:dictionaryId/w/:entryId'
export function routeShape(fullPath: string): string {
  return fullPath.replace(/\$(\w+)/g, ':$1').replace(/\/+$/, '') || '/';
}

// The card fields whose override an edit set (a value where the entry inherited, or a different
// one) and dropped ("restore shared version"), by name.
export function overrideChanges<T extends object>(before: T, after: T): { overridden: string; restored: string } {
  const overridden: string[] = [];
  const restored: string[] = [];
  for (const key of Object.keys(after) as (keyof T)[]) {
    const a = before[key] ?? null;
    const b = after[key] ?? null;
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    (b === null ? restored : overridden).push(String(key));
  }
  return { overridden: overridden.join(','), restored: restored.join(',') };
}
